import {
  Bell,
  CheckCircle2,
  Clock,
  Gift,
  Image,
  Inbox,
  type LucideIcon,
  MessageSquare,
} from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  type NotificationList,
  loadNotifications,
  readNotifications,
  useResource,
} from "@/lib/api";
import { formatDateTime } from "@/lib/labels";
import { cn } from "@/lib/utils";

/** How often the bell asks for news while the page is open. */
const REFRESH_MS = 60_000;

/**
 * What an entry asks of the reader (D94): a sign for the kind and the next step after the text,
 * »… hochgeladen. · Logo prüfen«. Here rather than in the stored text, so older entries get it
 * too. A kind without a step only reports.
 */
export const NOTIFICATION_STEPS: Readonly<
  Record<string, { readonly icon: LucideIcon; readonly step?: string }>
> = {
  sponsor_submitted: { icon: Inbox, step: "Türchen prüfen" },
  sponsor_approved: { icon: CheckCircle2 },
  sponsor_changes_requested: { icon: MessageSquare, step: "Wunsch ansehen" },
  sponsor_logo_uploaded: { icon: Image, step: "Logo prüfen" },
  logo_unpublished: { icon: Image, step: "Logo prüfen" },
  submission_overdue: { icon: Clock, step: "Nachfassen" },
  approval_overdue: { icon: Clock, step: "Nachfassen" },
  prize_unclaimed: { icon: Gift, step: "Neu ziehen" },
  prize_confirmed: { icon: Gift, step: "Gewinn übermitteln" },
};

/**
 * The bell in the head of the backoffice (D56): what the operator's permissions see, newest
 * first, unread in bold. A click marks the entry read and opens its page; »Alle gelesen« marks
 * the rest. Read is per person, the server keeps it. The list refreshes every minute, so a
 * sponsor's submission shows up without a reload.
 */
export function NotificationBell(): ReactElement {
  const resource = useResource<NotificationList>(loadNotifications);
  const [fresh, setFresh] = useState<NotificationList | null>(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { reload } = resource;

  useEffect(() => {
    const timer = window.setInterval(reload, REFRESH_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [reload]);

  // A reload brings the server's list; until then the answer of the last »read« stands.
  const loaded = resource.status === "ready" ? resource.data : null;
  useEffect(() => {
    setFresh(null);
  }, [loaded]);

  const list = fresh ?? loaded;
  const unread = list?.unread ?? 0;

  const read = (ids?: readonly string[]): void => {
    void readNotifications(ids).then((next) => {
      if (next !== null) {
        setFresh(next);
      }
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={
            unread === 0
              ? "Benachrichtigungen"
              : `Benachrichtigungen, ${String(unread)} ungelesen`
          }
          className="relative"
        >
          <Bell aria-hidden="true" />
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 right-0.5 min-w-4 rounded-full bg-destructive px-1 text-[0.625rem] leading-4 font-bold text-white"
            >
              {unread > 99 ? "99+" : String(unread)}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2">
          <p className="text-sm font-bold">Benachrichtigungen</p>
          {unread > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                read();
              }}
            >
              Alle gelesen
            </Button>
          ) : null}
        </div>
        {list === null || list.entries.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">Keine Benachrichtigungen.</p>
        ) : (
          <ul className="max-h-[60dvh] overflow-y-auto">
            {list.entries.map((entry) => {
              const { icon: Icon, step } = NOTIFICATION_STEPS[entry.kind] ?? { icon: Bell };
              return (
                <li key={entry.notification_id} className="border-b last:border-b-0">
                  <button
                    type="button"
                    className={cn(
                      "grid w-full grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 px-4 py-2 text-left text-sm hover:bg-muted focus-visible:outline-3 focus-visible:outline-offset-[-3px] focus-visible:outline-ring",
                      entry.read ? "text-muted-foreground" : "font-semibold text-foreground",
                    )}
                    onClick={() => {
                      if (!entry.read) {
                        read([entry.notification_id]);
                      }
                      setOpen(false);
                      if (entry.link !== null) {
                        void navigate(entry.link);
                      }
                    }}
                  >
                    <Icon
                      aria-hidden="true"
                      className="row-span-2 mt-0.5 size-4 text-muted-foreground"
                    />
                    <span>
                      {entry.text}
                      {step === undefined ? null : (
                        <span className="font-bold text-foreground"> · {step}</span>
                      )}
                    </span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {entry.action_title} · {formatDateTime(entry.created_at)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
